import { useState } from "react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { useNavigate } from "react-router-dom";
import { notifyError, notifySuccess } from "../utils/notify";

// Input validation helpers
const validators = {
  teamName: (name) => {
    if (!name || typeof name !== "string") return "Teamname ist erforderlich";
    const trimmed = name.trim();
    if (trimmed.length < 2) return "Teamname muss mindestens 2 Zeichen lang sein";
    if (trimmed.length > 50) return "Teamname darf maximal 50 Zeichen lang sein";
    if (!/^[a-zA-Z0-9äöüÄÖÜß\s\-'.]+$/.test(trimmed)) {
      return "Teamname enthält ungültige Zeichen";
    }
    return null;
  },
  email: (email) => {
    if (!email) return "E-Mail ist erforderlich";
    if (typeof email !== "string") return "E-Mail muss ein Text sein";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) return "Ungültiges E-Mail-Format";
    if (email.length > 100) return "E-Mail ist zu lang";
    return null;
  },
  // Handynummer ist optional: nur pruefen, wenn ueberhaupt etwas eingegeben
  // wurde. Bewusst liberal (Ziffern, Leerzeichen, + / - ( )), damit gaengige
  // Schreibweisen wie "+43 660 1234567" oder "0660/1234567" durchgehen.
  phone: (phone) => {
    if (!phone || !phone.trim()) return null;
    if (typeof phone !== "string") return "Telefonnummer muss ein Text sein";
    const trimmed = phone.trim();
    if (trimmed.length > 30) return "Telefonnummer ist zu lang";
    if (!/^[0-9+\-/\s()]+$/.test(trimmed)) return "Telefonnummer enthält ungültige Zeichen";
    return null;
  },
  memberName: (name, index) => {
    if (!name) return null; // Optional nur fuer Mitglied 5
    if (typeof name !== "string") return `Mitglied ${index + 1} muss ein Text sein`;
    if (name.trim().length > 50) return `Name von Mitglied ${index + 1} ist zu lang`;
    if (!/^[a-zA-Z0-9äöüÄÖÜß\s\-'.]+$/.test(name.trim())) {
      return `Mitglied ${index + 1} enthält ungültige Zeichen`;
    }
    return null;
  },
  age: (age, index) => {
    const numAge = parseInt(age);
    if (isNaN(numAge)) return `Alter von Mitglied ${index + 1} muss eine Zahl sein`;
    if (numAge < 1 || numAge > 120) return `Alter von Mitglied ${index + 1} muss zwischen 1 und 120 liegen`;
    return null;
  },
};

export default function AddTeamModal({ open, onClose, onSaved, existingTeams }) {
  const [teamName, setTeamName] = useState("");
  const [eventType, setEventType] = useState("Kinder");
  const [teamType, setTeamType] = useState("Männer");
  const [members, setMembers] = useState(["", "", "", "", ""]);
  const [ages, setAges] = useState([0, 0, 0, 0, 0]);
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};

    // Validate team name
    const teamNameError = validators.teamName(teamName);
    if (teamNameError) newErrors.teamName = teamNameError;

    // Check for duplicate team name
    if (existingTeams.some((t) => t.team_name?.toLowerCase() === teamName.trim().toLowerCase())) {
      newErrors.teamName = "Ein Team mit diesem Namen existiert bereits";
    }

    // Validate team type
    if (!teamType) {
      newErrors.teamType = "Teamtyp ist erforderlich";
    }

    // Validate email
    const emailError = validators.email(contactEmail);
    if (emailError) newErrors.email = emailError;

    // Validate phone (optional)
    const phoneError = validators.phone(contactPhone);
    if (phoneError) newErrors.phone = phoneError;

    // Validate members (Mitglied 1-4 sind erforderlich, Mitglied 5 optional)
    for (let idx = 0; idx < 4; idx++) {
      if (!members[idx].trim()) {
        newErrors[`member_${idx}`] = `Mitglied ${idx + 1} ist erforderlich`;
      }
    }
    members.forEach((member, idx) => {
      if (!member.trim()) return;
      const memberError = validators.memberName(member, idx);
      if (memberError) newErrors[`member_${idx}`] = memberError;
    });
    const filledMembers = members.filter((m) => m.trim());

    // Validate ages for children events
    if (eventType === "Kinder") {
      filledMembers.forEach((member, idx) => {
        const ageError = validators.age(ages[idx], idx);
        if (ageError) newErrors[`age_${idx}`] = ageError;
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      notifyError("Bitte behebe die Fehler im Formular");
      return;
    }

    const payload = {
      team_name: teamName.trim(),
      event_type: eventType,
      team_type: teamType,
      member1: members[0]?.trim() || "",
      member2: members[1]?.trim() || "",
      member3: members[2]?.trim() || "",
      member4: members[3]?.trim() || "",
      member5: members[4]?.trim() || null,
      contact_email: contactEmail.trim(),
      contact_phone: contactPhone.trim() || null,
    };

    if (eventType === "Kinder") {
      payload.member1_age = parseInt(ages[0]) || 0;
      payload.member2_age = parseInt(ages[1]) || 0;
      payload.member3_age = parseInt(ages[2]) || 0;
      payload.member4_age = ages[3] > 0 ? parseInt(ages[3]) : null;
      payload.member5_age = ages[4] > 0 ? parseInt(ages[4]) : null;
    }

    setIsLoading(true);
    try {
      const data = await fetchWithAuth(`${BACKEND_URL}/teams`, navigate, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!data || data.error) {
        console.warn("[AddTeamModal] Backend error:", data);
        notifyError(data?.error || "Team konnte nicht erstellt werden");
        return;
      }

      onSaved();
      onClose();
      notifySuccess("Team erfolgreich erstellt!");

      // Reset form
      setTeamName("");
      setEventType("Kinder");
      setTeamType("Männer");
      setMembers(["", "", "", "", ""]);
      setAges([0, 0, 0, 0, 0]);
      setContactEmail("");
      setContactPhone("");
      setErrors({});
    } catch (err) {
      console.error("[AddTeamModal] Save error:", err);
      notifyError("Team konnte nicht gespeichert werden: " + (err.message || "Unbekannter Fehler"));
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-secondary-950 bg-opacity-75 z-50">
      <div className="bg-secondary-900 p-6 rounded shadow-lg w-full max-w-md text-white border border-secondary-800 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4 text-white">Neues Team hinzufügen</h2>

        {/* Team Name */}
        <div className="mb-3">
          <input
            type="text"
            placeholder="Teamname"
            value={teamName}
            onChange={(e) => {
              setTeamName(e.target.value);
              if (errors.teamName) setErrors({ ...errors, teamName: null });
            }}
            disabled={isLoading}
            className={`w-full bg-secondary-800 text-white border p-2 mb-1 rounded placeholder-gray-400 focus:outline-none focus:ring-2 ${
              errors.teamName ? "border-status-error focus:ring-status-error" : "border-secondary-700 focus:ring-primary-500"
            }`}
          />
          {errors.teamName && <p className="text-status-error text-sm">{errors.teamName}</p>}
        </div>

        {/* Event Type */}
        <div className="mb-3">
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            disabled={isLoading}
            className="w-full bg-secondary-800 text-white border border-secondary-700 p-2 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="Kinder" className="bg-secondary-800">
              Kinder
            </option>
            <option value="Erwachsene" className="bg-secondary-800">
              Erwachsene
            </option>
          </select>
        </div>

        {/* Team Type */}
        <div className="mb-3">
          <select
            value={teamType}
            onChange={(e) => {
              setTeamType(e.target.value);
              if (errors.teamType) setErrors({ ...errors, teamType: null });
            }}
            disabled={isLoading}
            className={`w-full bg-secondary-800 text-white border p-2 rounded focus:outline-none focus:ring-2 ${
              errors.teamType ? "border-status-error focus:ring-status-error" : "border-secondary-700 focus:ring-primary-500"
            }`}
          >
            <option value="">Bitte wählen</option>
            <option value="Männer">Männer</option>
            <option value="Frauen">Frauen</option>
            <option value="Mixed">Gemischt</option>
          </select>
          {errors.teamType && <p className="text-status-error text-sm">{errors.teamType}</p>}
        </div>

        {/* Members */}
        {errors.members && <p className="text-status-error text-sm mb-2">{errors.members}</p>}
        {members.map((m, i) => (
          <div key={i} className="flex space-x-2 mb-2">
            <input
              type="text"
              placeholder={`Mitglied ${i + 1}${i === 4 ? " (optional)" : ""}`}
              value={members[i]}
              onChange={(e) => {
                const newMembers = [...members];
                newMembers[i] = e.target.value;
                setMembers(newMembers);
                if (errors[`member_${i}`]) {
                  const newErrors = { ...errors };
                  delete newErrors[`member_${i}`];
                  setErrors(newErrors);
                }
              }}
              disabled={isLoading}
              className={`flex-1 bg-secondary-800 text-white border p-2 rounded placeholder-gray-400 focus:outline-none focus:ring-2 ${
                errors[`member_${i}`] ? "border-status-error focus:ring-status-error" : "border-secondary-700 focus:ring-primary-500"
              }`}
            />
            {eventType === "Kinder" && (
              <input
                type="number"
                placeholder="Alter"
                value={ages[i]}
                onChange={(e) => {
                  const newAges = [...ages];
                  newAges[i] = parseInt(e.target.value) || 0;
                  setAges(newAges);
                  if (errors[`age_${i}`]) {
                    const newErrors = { ...errors };
                    delete newErrors[`age_${i}`];
                    setErrors(newErrors);
                  }
                }}
                disabled={isLoading}
                className={`w-20 bg-secondary-800 text-white border p-2 rounded placeholder-gray-400 focus:outline-none focus:ring-2 ${
                  errors[`age_${i}`] ? "border-status-error focus:ring-status-error" : "border-secondary-700 focus:ring-primary-500"
                }`}
              />
            )}
          </div>
        ))}
        {Object.keys(errors)
          .filter((key) => key.startsWith("member_") || key.startsWith("age_"))
          .map((key) => (
            <p key={key} className="text-status-error text-sm mb-1">
              {errors[key]}
            </p>
          ))}

        {/* Email */}
        <div className="mb-4">
          <input
            type="email"
            placeholder="Kontakt E-Mail"
            value={contactEmail}
            onChange={(e) => {
              setContactEmail(e.target.value);
              if (errors.email) setErrors({ ...errors, email: null });
            }}
            disabled={isLoading}
            className={`w-full bg-secondary-800 text-white border p-2 mb-1 rounded placeholder-gray-400 focus:outline-none focus:ring-2 ${
              errors.email ? "border-status-error focus:ring-status-error" : "border-secondary-700 focus:ring-primary-500"
            }`}
          />
          {errors.email && <p className="text-status-error text-sm">{errors.email}</p>}
        </div>

        {/* Phone (optional) */}
        <div className="mb-4">
          <input
            type="tel"
            placeholder="Kontakt Telefon (optional)"
            value={contactPhone}
            onChange={(e) => {
              setContactPhone(e.target.value);
              if (errors.phone) setErrors({ ...errors, phone: null });
            }}
            disabled={isLoading}
            className={`w-full bg-secondary-800 text-white border p-2 mb-1 rounded placeholder-gray-400 focus:outline-none focus:ring-2 ${
              errors.phone ? "border-status-error focus:ring-status-error" : "border-secondary-700 focus:ring-primary-500"
            }`}
          />
          {errors.phone && <p className="text-status-error text-sm">{errors.phone}</p>}
        </div>

        {/* Buttons */}
        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 rounded bg-secondary-700 hover:bg-secondary-600 text-gray-200 transition-colors disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="px-4 py-2 rounded bg-primary-500 text-secondary-950 hover:bg-primary-600 transition-colors disabled:opacity-50"
          >
            {isLoading ? "Speichern..." : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
